import { toast } from "react-toastify";
import type {
  ICreateObservation,
  IChangePatientRelease,
  INewAttendanceRangeParams,
  IListAppointmentParams,
  IListAuthorizationParams,
  ITreatmentExamsParams,
  IPatientTreatmentExamsParams,
  IPatientExamsDetails,
  ITreatmentExamsResponse,
  IDetailsExams,
  ICreateObservationResponse,
  IShowObservation,
  INewAttendanceRangeResponse,
  IListAppointmentResponse,
  IListAuthorizationResponse,
  IChangePatientReleaseResponse,
  IPatientExamsDetailsResponse,
  IPatientTreatmentExamsResponse,
  IGetLastAppointmentParams,
  IGetLastAppointmentResponse,
  IUpdateRequestAdditionExamExamsIdParams,
  IPacientProtocolParams,
  IPacientProtocolResponse,
} from "./types";
import api from "../../services/api";
import type { AxiosResponse } from "axios";
import new_api from "../../services/newApi/api";

export function createExamUtils(
  setIsLoading: (v: boolean) => void,
  _setIsDetailExamsLoading: (v: boolean) => void,
) {
  return {
    async treatmentExams(
      uuid: string,
      numberTreatment: string,
      params: ITreatmentExamsParams,
    ) {
      try {
        const response = await api.get<ITreatmentExamsResponse>(
          `/treatment/exams/${uuid}/${numberTreatment}`,

          { params },
        );
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async treatmentUnitExams(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      seqexame: string,
      name: string,
    ) {
      try {
        const response = await api.get<string[]>(
          `/treatment/exam/${uuid}/${numberTreatment}/${r_id}/${seqexame}?name=${name}`,
        );
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async treatmentUnitExamsV2(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      seqexame: string,
      name: string,
      tipo?: number,
    ) {
      try {
        const response: AxiosResponse<ArrayBuffer> = await api.get(
          `/treatment/exam/v2/${uuid}/${numberTreatment}/${r_id}/${seqexame}`,
          { responseType: "arraybuffer", params: { name, ...(tipo !== undefined ? { Tipo: tipo } : {}) } },
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async detailsExams(
      uuid: string,
      numberTreatment: string,
      userName: string,
      tipo?: number,
    ) {
      setIsLoading(true);

      try {
        const response = await api.get<IDetailsExams>(
          `/treatment/details/${uuid}/${numberTreatment}`,
          { params: { userName, ...(tipo !== undefined ? { Tipo: tipo } : {}) } },
        );
        return response?.data;
      } catch (error: any) {
        console.error("[detailsExams] error for uuid:", uuid, "| status:", error?.response?.status, "| body:", error?.response?.data);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async completeReport(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      name: string,
    ) {
      try {
        const response = await api.get<string[]>(
          `/treatment/report/${uuid}/${numberTreatment}/${r_id}?name=${name}`,
        );
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async completeReportV2(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      name: string,
      tipo?: number,
    ) {
      try {
        const response = await api.get<AxiosResponse<ArrayBuffer>>(
          `/treatment/report/v2/${uuid}/${numberTreatment}/${r_id}`,
          { responseType: "arraybuffer", params: { name, ...(tipo !== undefined ? { Tipo: tipo } : {}) } },
        );

        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async pacientProtocol(data: IPacientProtocolParams) {
      try {
        const response = await new_api.post<IPacientProtocolResponse>(`users/protocol/print`, data);
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async createObservation(data: ICreateObservation) {
      try {
        const response = await api.post<ICreateObservationResponse>(
          `create/observation`,
          data,
        );

        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async showObservation(data: number) {
      const response = await api.get<IShowObservation>(
        `show/observation/${data}`,
      );
      return response;
    },

    async newAttendanceRange(
      uuid: string,
      startDate: string,
      endDate: string,
      params: INewAttendanceRangeParams,
    ) {
      setIsLoading(true);

      try {
        const response = await api.get<INewAttendanceRangeResponse>(
          `/v2/list/attendance/range/${uuid}/${startDate}/${endDate}`,
          { params },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async newAttendanceRangeNoOverlay(
      uuid: string,
      startDate: string,
      endDate: string,
      params: INewAttendanceRangeParams,
    ) {
      try {
        const response = await api.get<INewAttendanceRangeResponse>(
          `/v2/list/attendance/range/${uuid}/${startDate}/${endDate}`,
          { params },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async listAppointment(
      uuid: string,
      type: number,
      params: IListAppointmentParams,
    ) {
      setIsLoading(true);
      try {
        const response = await api.get<IListAppointmentResponse>(
          `/v2/list/appointment/${uuid}/${type}`,
          { params },
        );
        const data = response.data;
        const appointmentPage = data?.Appointment?.[0];

        if (!appointmentPage?.appointment_list) {
          return data;
        }

        appointmentPage.appointment_list = appointmentPage.appointment_list.map(
          (appointment) => {
            if (!appointment.ds_status_cancelamento) {
              return appointment;
            }

            return {
              ...appointment,
              cancellation: {
                status: appointment.ds_status_cancelamento,
                reason: appointment.ds_motivo_cancelamento,
              },
            };
          },
        );

        return data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async listAuthorization(
      uuid: string,
      type: number,
      params: IListAuthorizationParams,
    ) {
      setIsLoading(true);
      try {
        const response = await api.get<IListAuthorizationResponse>(
          `/v2/list/authorization/${uuid}/${type}`,
          { params },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async changePatientRelease(data: IChangePatientRelease) {
      setIsLoading(true);

      try {
        const response = await api.post<IChangePatientReleaseResponse>(
          `/v2/patientrelease`,
          data,
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async patientExamsDetails(
      uuid: string,
      numberTreatment: string,
      params: IPatientExamsDetails,
    ) {
      setIsLoading(true);
      try {
        const response = await api.get<IPatientExamsDetailsResponse>(
          `/treatment/details/${uuid}/${numberTreatment}`,
          {
            params,
          },
        );
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async patientTreatmentExams(
      uuid: string,
      numberTreatment: string,
      params: IPatientTreatmentExamsParams,
    ) {
      setIsLoading(true);
      try {
        const response = await api.get<IPatientTreatmentExamsResponse>(
          `/treatment/exams/${uuid}/${numberTreatment}`,
          {
            params,
          },
        );
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async patientCompleteReport(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      name: string,
    ) {
      setIsLoading(true);
      try {
        const response = await api.get<string[]>(
          `/treatment/report/${uuid}/${numberTreatment}/${r_id}?name=${name}`,
        );

        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async patientCompleteReportV2(
      uuid: string,
      numberTreatment: string,
      r_id: string,
      name: string,
    ) {
      setIsLoading(true);
      try {
        const response: AxiosResponse<ArrayBuffer> = await api.get(
          `/treatment/report/v2/${uuid}/${numberTreatment}/${r_id}?name=${name}`,
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async getLastAppointment(params: IGetLastAppointmentParams) {
      try {
        setIsLoading(true);
        const response: IGetLastAppointmentResponse = (
          await api.get(`/treatment/last`, {
            params,
          })
        ).data.data;
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    async getPatientAppointments(params: IGetLastAppointmentParams) {
      try {
        setIsLoading(true);
        const response: IGetLastAppointmentResponse = (
          await api.get(`/treatment/appointments`, {
            params,
          })
        ).data.data;
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      } finally {
        setIsLoading(false);
      }
    },

    // spec 019-addition-status-tasy-first, operator follow-up: no `catch`
    // here on purpose — every other method in this file swallows its own
    // error and toasts, but this one's sole caller
    // (`AdditionRequestTabV2/index.tsx`'s `refuseRequest` mutation) needs
    // the rejection itself to reach react-query's `onError`, which is what
    // rolls back the optimistic `status: 0` write and is now the only
    // place that toasts for this failure — toasting here too would double
    // it. `finally` alone still resets `isLoading` on both the success and
    // the failure path; a `catch` that only rethrows is what
    // `no-useless-catch` exists to flag. Confirmed via grep: this is the
    // only caller in `src/`, so nothing else depends on the old
    // swallow-and-succeed behaviour.
    async updateRequestAdditionExamExamsId(
      requestAdditionExamExamsId: number,
      params: IUpdateRequestAdditionExamExamsIdParams,
    ) {
      try {
        setIsLoading(true);
        await new_api.put(
          `/addition/${requestAdditionExamExamsId}`,
          params,
        );
      } finally {
        setIsLoading(false);
      }
    },
  };
}
